/**
 * Trial and subscription access — docs/pricing-v2.md (API repo).
 *
 * The API sends `vendor.access` on /users/me and `access` on plans/my-plan:
 * `active` (paying, or a legacy vendor), `trial` (with days left once the
 * clock has started at approval) or `expired` (a trial or a paid
 * subscription ended). The server already refuses what an expired vendor may
 * not do; the backoffice shows the trial bar and the plan wall from this.
 */

export type AccessState = 'active' | 'trial' | 'expired';

export interface VendorAccess {
  state: AccessState;
  trialEndsAt: string | null;
  daysLeft: number | null;
  reason: 'trial_ended' | 'subscription_ended' | null;
}

export interface ContractSummary {
  termMonths: number;
  billingInterval: 'month' | 'year';
  monthlyAmount: number;
  currency: string;
  startDate: string | null;
  endDate: string | null;
  autoRenew: boolean;
  noticeDays: number;
}

export const ACTIVE_ACCESS: VendorAccess = { state: 'active', trialEndsAt: null, daysLeft: null, reason: null };

/** The access block off a vendor; anything unreadable is `active` — never lock a vendor on a missing field. */
export function readAccess(vendor: unknown): VendorAccess {
  const raw = (vendor as { access?: Partial<VendorAccess> } | null | undefined)?.access;
  if (!raw || (raw.state !== 'trial' && raw.state !== 'expired' && raw.state !== 'active')) return ACTIVE_ACCESS;
  return {
    state: raw.state,
    trialEndsAt: raw.trialEndsAt ?? null,
    daysLeft: typeof raw.daysLeft === 'number' ? raw.daysLeft : null,
    reason: raw.reason ?? null,
  };
}

export const isTrialPlan = (plan: { slug?: string; pricingType?: string } | null | undefined): boolean =>
  !!plan && (plan.pricingType === 'trial' || plan.slug === 'trial');

/**
 * Pages an expired vendor still reaches: choosing and paying for a plan, the
 * team and devices (to sign someone out), notifications. Everything else shows
 * the plan wall — the data stays, it is just not worked on until a plan is chosen.
 */
const OPEN_WHEN_EXPIRED = ['/home/billing', '/home/team', '/home/devices', '/home/notifications'];

export const isOpenWhenExpired = (pathname: string): boolean =>
  OPEN_WHEN_EXPIRED.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/** A trial in its last days reads as urgent. */
export const TRIAL_URGENT_DAYS = 3;
